import React, { useState, useEffect, useMemo, useCallback } from "react";
import { MenuItem, TextField } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";

import CreateAssessmentModal from "../components/assessments/CreateAssessmentModal";
import UpdateAssessmentModal from "../components/assessments/UpdateAssessmentModal";
import ViewAssessmentModal from "../components/assessments/ViewAssessmentModal";
import AssessmentScoreRecordModal from "../components/assessmentScores/AssessmentScoreRecordModal";
import RowActionMenu, {
  RowActionDef,
} from "../components/common/RowActionMenu";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getAssessments,
  resetAssessmentServerStatus,
} from "../redux/actions/assessmentActions";
import { getClassrooms } from "../redux/actions/classroomActions";
import { usePermissions } from "../hooks/usePermissions";
import { AssessmentResponseDto } from "../shared/dtos/assessment/AssessmentResponseDto";

const AssessmentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: assessments,
    loadingFetchAll: loadingAssessments,
    pagination,
    serverStatus,
    loadingCreate: loadingAssessmentCreate,
    errorCreate: errorCreatingAssessment,
    loadingUpdate: loadingAssessmentUpdate,
    errorUpdate: errorUpdatingAssessment,
  } = useAppSelector((state) => state.assessment);

  const { items: classrooms } = useAppSelector((state) => state.classroom);

  // Filters and Pagination State
  const [filters, setFilters] = useState<{
    classroomId: string;
    termId: string;
  }>({
    classroomId: "",
    termId: "",
  });
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });
  const [selectedAssessment, setSelectedAssessment] =
    useState<AssessmentResponseDto | null>(null);

  // Modal Control State
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState<boolean>(false);
  const [isViewOpen, setIsViewOpen] = useState<boolean>(false);
  const [isScoreGridOpen, setIsScoreGridOpen] = useState<boolean>(false);

  // Permissions
  const canCreate = hasPermission("assessment:create");
  const canUpdate = hasPermission("assessment:update");
  const canScore = hasPermission("assessment_score:create");

  useEffect(() => {
    if (!classrooms || classrooms.length === 0) {
      dispatch(getClassrooms());
    }
  }, [dispatch, classrooms]);

  const fetchAssessments = useCallback((): void => {
    dispatch(
      getAssessments({
        page: pageConfig.page,
        size: pageConfig.limit,
        ...filters,
      }),
    );
  }, [dispatch, pageConfig, filters]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  const handleOpenView = (selectedRow: AssessmentResponseDto): void => {
    setSelectedAssessment(selectedRow);
    setIsViewOpen(true);
  };

  const handleOpenGrid = (selectedRow: AssessmentResponseDto): void => {
    setSelectedAssessment(selectedRow);
    setIsScoreGridOpen(true);
  };

  const handleOpenUpdate = (selectedRow: AssessmentResponseDto): void => {
    setSelectedAssessment(selectedRow);
    setIsUpdateOpen(true);
  };

  // Handle Server Status Auto-Close
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) setIsCreateOpen(false);
    if (operation === "update" && status === 200) setIsUpdateOpen(false);

    dispatch(resetAssessmentServerStatus());
  }, [serverStatus, dispatch]);

  const columns = useMemo<TableColumn<AssessmentResponseDto>[]>(
    () => [
      { id: "name", label: "Assessment Name", minWidth: 200 },
      { id: "subjectName", label: "Subject", minWidth: 120 },
      { id: "classroomName", label: "Classroom", minWidth: 120 },
      { id: "maxMarks", label: "Max Marks", minWidth: 80 },
      {
        id: "weightage",
        label: "Weight",
        minWidth: 80,
        format: (v) => `${v ?? 100}%`,
      },
      { id: "termName", label: "Term", minWidth: 100 },
    ],
    [],
  );

  const renderRowActions = (row: AssessmentResponseDto): React.ReactNode => {
    const inlineActions: RowActionDef[] = [
      {
        label: "View",
        icon: <VisibilityOutlinedIcon fontSize="small" />,
        color: "info",
        onClick: () => handleOpenView(row),
        show: true,
      },
    ];

    const menuActions: RowActionDef[] = [
      {
        label: "Enter Marks",
        icon: <AssignmentTurnedInOutlinedIcon fontSize="small" />,
        color: "success",
        onClick: () => handleOpenGrid(row),
        show: canScore,
      },
      {
        label: "Edit Assessment",
        icon: <EditOutlinedIcon fontSize="small" />,
        onClick: () => handleOpenUpdate(row),
        show: canUpdate,
      },
    ];

    return (
      <RowActionMenu inlineActions={inlineActions} menuActions={menuActions} />
    );
  };

  return (
    <PageContainer
      title="Assessments & Exams"
      subtitle="Manage exam configurations and student mark entry."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsCreateOpen(true)}
          >
            New Assessment
          </AppButton>
        )
      }
    >
      <PageToolbar>
        <TextField
          select
          label="Classroom"
          size="small"
          sx={{ minWidth: 200 }}
          value={filters.classroomId}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, classroomId: e.target.value }));
            setPageConfig((prev) => ({ ...prev, page: 1 }));
          }}
        >
          <MenuItem value="">All Classes</MenuItem>
          {classrooms?.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
      </PageToolbar>

      <StandardTable<AssessmentResponseDto>
        columns={columns}
        data={assessments}
        loading={loadingAssessments}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        onPageChange={(p) => setPageConfig((prev) => ({ ...prev, page: p }))}
        onRowsPerPageChange={(l) => setPageConfig({ page: 1, limit: l })}
        rowAction={renderRowActions}
      />

      {/* CREATE MODAL */}
      {isCreateOpen && (
        <CreateAssessmentModal
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          loading={loadingAssessmentCreate}
          error={errorCreatingAssessment}
        />
      )}

      {/* UPDATE, VIEW & SCORE RECORD MODALS */}
      {selectedAssessment && (
        <>
          <UpdateAssessmentModal
            open={isUpdateOpen}
            onClose={() => setIsUpdateOpen(false)}
            assessment={selectedAssessment}
            loading={loadingAssessmentUpdate}
            error={errorUpdatingAssessment}
          />
          <ViewAssessmentModal
            open={isViewOpen}
            onClose={() => setIsViewOpen(false)}
            assessment={selectedAssessment}
          />
          <AssessmentScoreRecordModal
            open={isScoreGridOpen}
            onClose={() => setIsScoreGridOpen(false)}
            assessment={selectedAssessment}
          />
        </>
      )}
    </PageContainer>
  );
};

export default AssessmentsPage;
